import React, { useRef, useState, useEffect } from "react";
import {
  Form,
  Button,
  Card,
  Alert,
  Container,
  Row,
  Col,
  Modal,
  Badge,
  ProgressBar,
  Accordion,
  FloatingLabel,
  Nav,
  Tab,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";
import { fires, auth, storageRef } from "../firebase";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import { Helmet } from "react-helmet";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TimezoneSelect from "react-timezone-select";
import { AudioRecorder, useAudioRecorder } from "react-audio-voice-recorder";
import ReactAudioPlayer from "react-audio-player";
import Autocomplete from "react-google-autocomplete";
import { 
  FaPlus, 
  FaTrash, 
  FaEye, 
  FaUpload, 
  FaMicrophone, 
  FaClock, 
  FaMapMarkerAlt,
  FaImage,
  FaVideo,
  FaTag,
  FaCheck,
  FaTimes,
  FaSave,
  FaArrowLeft,
  FaImages,
  FaList,
  FaShieldAlt,
  FaUser,
  FaChair,
} from "react-icons/fa";

export default function AdminTourismEventCreator() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const maxW = isMobile ? "100%" : "90%";

  // Form refs
  const eventTitleRef = useRef();
  const eventDescriptionRef = useRef();
  const eventShortDescriptionRef = useRef();
  const eventLocationRef = useRef();
  const eventAddressRef = useRef();
  const eventCityRef = useRef();
  const eventProvinceRef = useRef();
  const eventCountryRef = useRef();
  const eventPostalCodeRef = useRef();
  const eventWebsiteRef = useRef();
  const eventPhoneRef = useRef();
  const eventEmailRef = useRef();
  const eventCurrencyRef = useRef();
  const eventCapacityRef = useRef();
  const eventDurationRef = useRef();
  const eventDifficultyRef = useRef();
  const eventAgeMinRef = useRef();
  const eventAgeMaxRef = useRef();
  const eventAccessibilityRef = useRef();
  const eventLanguagesRef = useRef();
  const eventIncludedRef = useRef();
  const eventNotIncludedRef = useRef();
  const eventWhatToBringRef = useRef();
  const eventCancellationPolicyRef = useRef();
  const eventRefundPolicyRef = useRef();
  const eventTermsConditionsRef = useRef();
  const eventTagsRef = useRef();
  const eventVideoURLRef = useRef();
  const fileInputRef = useRef();
  // Price refs
  const priceNameRef = useRef();
  const priceAmountRef = useRef();
  const priceStartDateRef = useRef();
  const priceEndDateRef = useRef();
  // Client Information refs
  const eventClientNameRef = useRef();
  const eventClientEmailRef = useRef();
  const eventClientPhoneRef = useRef();
  const eventClientDescriptionRef = useRef();
  // Price currency selector
  const priceCurrencyRef = useRef();

  // State variables
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [activeTab, setActiveTab] = useState("basic");
  const [showToast, setShowToast] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);

  // Event data
  const [eventData, setEventData] = useState({
    title: "",
    description: "",
    shortDescription: "",
    location: "",
    address: "",
    city: "",
    province: "",
    country: "",
    postalCode: "",
    website: "",
    phone: "",
    email: "",
    prices: [
      {
        id: "default",
        name: "Standard Price",
        amount: 0,
        startDate: new Date(),
        endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
        isActive: true
      }
    ],
    currency: "CAD",
    capacity: 50,
    duration: 2,
    difficulty: "Easy",
    ageMin: 0,
    ageMax: 100,
    accessibility: "",
    languages: ["English"],
    included: [],
    notIncluded: [],
    whatToBring: [],
    openingHours: [],
    images: [],
    audioGuide: "",
    videoURL: "",
    tags: [],
    cancellationPolicy: "",
    refundPolicy: "",
    termsConditions: "",
    // Client Information
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    clientProfilePicture: "",
    clientDescription: "",
    isActive: false,
    isFeatured: false,
    createdAt: firebase.firestore.Timestamp.fromDate(new Date()),
    updatedAt: firebase.firestore.Timestamp.fromDate(new Date()),
  });

  // Opening hours state
  const [openingHours, setOpeningHours] = useState([
    { day: "Monday", open: "09:00", close: "17:00", isOpen: true },
    { day: "Tuesday", open: "09:00", close: "17:00", isOpen: true },
    { day: "Wednesday", open: "09:00", close: "17:00", isOpen: true },
    { day: "Thursday", open: "09:00", close: "17:00", isOpen: true },
    { day: "Friday", open: "09:00", close: "17:00", isOpen: true },
    { day: "Saturday", open: "10:00", close: "18:00", isOpen: true },
    { day: "Sunday", open: "10:00", close: "18:00", isOpen: true },
  ]);

  // Image upload states
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);
  // Client profile picture state
  const [selectedClientProfilePicture, setSelectedClientProfilePicture] = useState(null);
  const [uploadingClientProfile, setUploadingClientProfile] = useState(false);

  // Audio recorder
  const audioRecorder = useAudioRecorder();

  // Modals
  const [showImageModal, setShowImageModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Check admin access
  useEffect(() => {
    if (!currentUser) {
      navigate("/");
      return;
    }

    fires
      .collection("admin")
      .doc(currentUser.uid)
      .get()
      .then((snapshot) => {
        if (snapshot.exists) {
          const adminInfo = snapshot.data();
          setAdminData(adminInfo);
        } else {
          setError("Access denied. Admin privileges required.");
          navigate("/");
        }
      })
      .catch((error) => {
        setError("Error checking admin access: " + error.message);
        navigate("/");
      });
  }, [currentUser, navigate]);

  // Debug Google Maps API key
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (apiKey) {
      console.log('Google Maps API key found:', apiKey.substring(0, 10) + '...');
    } else {
      console.warn('Google Maps API key not found. Address autocomplete will be disabled.');
    }
  }, []);

  // Handle opening hours change
  const handleOpeningHoursChange = (index, field, value) => {
    const updatedHours = [...openingHours];
    updatedHours[index] = { ...updatedHours[index], [field]: value };
    setOpeningHours(updatedHours);
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed the 4 photo limit
    const currentCount = selectedImages.length;
    const newCount = currentCount + files.length;
    
    if (newCount > 4) {
      setError(`Maximum 4 photos allowed. You currently have ${currentCount} photos and trying to add ${files.length} more.`);
      setShowToast(true);
      e.target.value = '';
      return;
    }
    
    // Append new files to existing selection instead of replacing
    setSelectedImages(prevImages => {
      const newImages = [...prevImages, ...files];
      return newImages;
    });
    
    // Clear the file input so the same files can be selected again if needed
    e.target.value = '';
  };

  // Handle client profile picture selection
  const handleClientProfilePictureSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedClientProfilePicture(file);
    }
  };

  // Upload images using custom API
  const uploadImages = async () => {
    if (selectedImages.length === 0) return [];

    setUploadingImages(true);
    setImageUploadProgress(0);
    const uploadedURLs = [];

    for (let i = 0; i < selectedImages.length; i++) {
      const file = selectedImages[i];
      
      try {
        const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': file.type
          },
          body: file
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success && data.downloadURL) {
          uploadedURLs.push(data.downloadURL);
          setImageUploadProgress(((i + 1) / selectedImages.length) * 100);
        } else {
          throw new Error('Upload failed: ' + (data.message || 'Unknown error'));
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        setError("Error uploading image: " + error.message);
        // Continue with other images even if one fails
      }
    }

    setUploadingImages(false);
    setSelectedImages([]);
    return uploadedURLs;
  };

  // Handle audio recording
  const handleAudioRecording = async (blob) => {
    try {
      // Convert blob to file
      const audioFile = new File([blob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
      
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': audioFile.type
        },
        body: audioFile
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.downloadURL) {
        setEventData({ ...eventData, audioGuide: data.downloadURL });
        setSuccess("Audio guide uploaded successfully!");
        setShowToast(true);
      } else {
        throw new Error('Audio upload failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error("Error uploading audio:", error);
      setError("Error uploading audio: " + error.message);
    }
  };

  // Upload client profile picture
  const uploadClientProfilePicture = async () => {
    if (!selectedClientProfilePicture) return null;

    setUploadingClientProfile(true);
    
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': selectedClientProfilePicture.type
        },
        body: selectedClientProfilePicture
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.downloadURL) {
        setEventData({ ...eventData, clientProfilePicture: data.downloadURL });
        setSelectedClientProfilePicture(null);
        setSuccess("Client profile picture uploaded successfully!");
        setShowToast(true);
        return data.downloadURL;
      } else {
        throw new Error('Upload failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error("Error uploading client profile picture:", error);
      setError("Error uploading client profile picture: " + error.message);
      return null;
    } finally {
      setUploadingClientProfile(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Upload images first
      const imageURLs = await uploadImages();
      
      // Upload client profile picture if selected
      let clientProfileURL = eventData.clientProfilePicture;
      if (selectedClientProfilePicture) {
        clientProfileURL = await uploadClientProfilePicture();
      }
      
      // Prepare event data
      const finalEventData = {
        ...eventData,
        images: [...eventData.images, ...imageURLs],
        clientProfilePicture: clientProfileURL,
        openingHours: openingHours,
        createdBy: currentUser.uid,
        createdByEmail: currentUser.email,
        adminId: currentUser.uid,
        status: "draft",
      };

      // Save to Firestore
      const docRef = await fires.collection("events").add(finalEventData);
      console.log(JSON.stringify(finalEventData))
      setCreatedEventId(docRef.id);
      setSuccess(`Tourism event created successfully! Event ID: ${docRef.id}`);
      setShowToast(true);
      
      // Reset form
      setEventData({
        title: "",
        description: "",
        shortDescription: "",
        location: "",
        address: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
        website: "",
        phone: "",
        email: "",
        prices: [
          {
            id: "default",
            name: "Standard Price",
            amount: 0,
            startDate: new Date(),
            endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
            isActive: true
          }
        ],
        currency: "CAD",
        capacity: 50,
        duration: 2,
        difficulty: "Easy",
        ageMin: 0,
        ageMax: 100,
        accessibility: "",
        languages: ["English"],
        included: [],
        notIncluded: [],
        whatToBring: [],
        openingHours: [],
        images: [],
        audioGuide: "",
        videoURL: "",
        tags: [],
        cancellationPolicy: "",
        refundPolicy: "",
        termsConditions: "",
        // Client Information
        clientName: "",
        clientEmail: "",
        clientPhone: "",
        clientProfilePicture: "",
        clientDescription: "",
        isActive: false,
        isFeatured: false,
        createdAt: firebase.firestore.Timestamp.fromDate(new Date()),
        updatedAt: firebase.firestore.Timestamp.fromDate(new Date()),
      });
      setOpeningHours([
        { day: "Monday", open: "09:00", close: "17:00", isOpen: true },
        { day: "Tuesday", open: "09:00", close: "17:00", isOpen: true },
        { day: "Wednesday", open: "09:00", close: "17:00", isOpen: true },
        { day: "Thursday", open: "09:00", close: "17:00", isOpen: true },
        { day: "Friday", open: "09:00", close: "17:00", isOpen: true },
        { day: "Saturday", open: "10:00", close: "18:00", isOpen: true },
        { day: "Sunday", open: "10:00", close: "18:00", isOpen: true },
      ]);
      setCreatedEventId(null);

      // Clear price form fields
      if (priceNameRef.current) priceNameRef.current.value = "";
      if (priceAmountRef.current) priceAmountRef.current.value = "";
      if (priceStartDateRef.current) priceStartDateRef.current.value = "";
      if (priceEndDateRef.current) priceEndDateRef.current.value = "";
    } catch (error) {
      setError("Error creating event: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Add tag
  const addTag = () => {
    const tag = eventTagsRef.current.value.trim();
    if (tag && !eventData.tags.includes(tag)) {
      setEventData({ ...eventData, tags: [...eventData.tags, tag] });
      eventTagsRef.current.value = "";
    }
  };

  // Remove tag
  const removeTag = (tagToRemove) => {
    setEventData({ ...eventData, tags: eventData.tags.filter(tag => tag !== tagToRemove) });
  };

  // Add included item
  const addIncludedItem = () => {
    const item = eventIncludedRef.current.value.trim();
    if (item && !eventData.included.includes(item)) {
      setEventData({ ...eventData, included: [...eventData.included, item] });
      eventIncludedRef.current.value = "";
    }
  };

  // Remove included item
  const removeIncludedItem = (itemToRemove) => {
    setEventData({ ...eventData, included: eventData.included.filter(item => item !== itemToRemove) });
  };

  // Add not included item
  const addNotIncludedItem = () => {
    const item = eventNotIncludedRef.current.value.trim();
    if (item && !eventData.notIncluded.includes(item)) {
      setEventData({ ...eventData, notIncluded: [...eventData.notIncluded, item] });
      eventNotIncludedRef.current.value = "";
    }
  };

  // Remove not included item
  const removeNotIncludedItem = (itemToRemove) => {
    setEventData({ ...eventData, notIncluded: eventData.notIncluded.filter(item => item !== itemToRemove) });
  };

  // Add what to bring item
  const addWhatToBringItem = () => {
    const item = eventWhatToBringRef.current.value.trim();
    if (item && !eventData.whatToBring.includes(item)) {
      setEventData({ ...eventData, whatToBring: [...eventData.whatToBring, item] });
      eventWhatToBringRef.current.value = "";
    }
  };

  // Remove what to bring item
  const removeWhatToBringItem = (itemToRemove) => {
    setEventData({ ...eventData, whatToBring: eventData.whatToBring.filter(item => item !== itemToRemove) });
  };

  // Add new price
  const addPrice = () => {
    const name = priceNameRef.current.value.trim();
    const amount = parseFloat(priceAmountRef.current.value) || 0;
    const startDate = priceStartDateRef.current.value ? new Date(priceStartDateRef.current.value) : new Date();
    const endDate = priceEndDateRef.current.value ? new Date(priceEndDateRef.current.value) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    const currency = priceCurrencyRef.current?.value || eventData.currency;

    if (!name) {
      setError("Price name is required");
      setShowToast(true);
      return;
    }

    if (amount < 0) {
      setError("Price amount cannot be negative");
      setShowToast(true);
      return;
    }

    if (startDate >= endDate) {
      setError("End date must be after start date");
      setShowToast(true);
      return;
    }

    const newPrice = {
      id: `price_${Date.now()}`,
      name: name,
      amount: amount,
      currency: currency,
      startDate: startDate,
      endDate: endDate,
      isActive: true
    };

    setEventData({
      ...eventData,
      prices: [...eventData.prices, newPrice]
    });

    // Clear form fields
    priceNameRef.current.value = "";
    priceAmountRef.current.value = "";
    priceStartDateRef.current.value = "";
    priceEndDateRef.current.value = "";

    setSuccess("Price added successfully!");
    setShowToast(true);
  };

  // Remove price
  const removePrice = (priceId) => {
    if (eventData.prices.length <= 1) {
      setError("At least one price is required");
      setShowToast(true);
      return;
    }

    setEventData({
      ...eventData,
      prices: eventData.prices.filter(price => price.id !== priceId)
    });

    setSuccess("Price removed successfully!");
    setShowToast(true);
  };

  // Update price field
  const updatePrice = (priceId, field, value) => {
    setEventData({
      ...eventData,
      prices: eventData.prices.map(price => 
        price.id === priceId ? { ...price, [field]: value } : price
      )
    });
  };

  // Toggle price active status
  const togglePriceActive = (priceId) => {
    setEventData({
      ...eventData,
      prices: eventData.prices.map(price => 
        price.id === priceId ? { ...price, isActive: !price.isActive } : price
      )
    });
  };

  // 1. Add a global sessionLength state
  const [sessionLength, setSessionLength] = useState(60);

  // 3. Function to generate time slots for a day
  function generateTimeSlots(open, close, length) {
    const slots = [];
    let [h, m] = open.split(":").map(Number);
    let [endH, endM] = close.split(":").map(Number);
    let start = h * 60 + m;
    const end = endH * 60 + endM;
    while (start + length <= end) {
      const slotH = Math.floor(start / 60).toString().padStart(2, '0');
      const slotM = (start % 60).toString().padStart(2, '0');
      slots.push(`${slotH}:${slotM}`);
      start += length;
    }
    return slots;
  }

  // --- Add state for new price opening hours and duration ---
  const defaultPriceOpeningHours = () => ({
    Monday: { isOpen: true, open: "09:00", close: "17:00" },
    Tuesday: { isOpen: true, open: "09:00", close: "17:00" },
    Wednesday: { isOpen: true, open: "09:00", close: "17:00" },
    Thursday: { isOpen: true, open: "09:00", close: "17:00" },
    Friday: { isOpen: true, open: "09:00", close: "17:00" },
    Saturday: { isOpen: true, open: "10:00", close: "18:00" },
    Sunday: { isOpen: true, open: "10:00", close: "18:00" },
  });
  const [newPriceDuration, setNewPriceDuration] = useState(60);
  const [newPriceOpeningHours, setNewPriceOpeningHours] = useState(defaultPriceOpeningHours());
  const [showPriceHours, setShowPriceHours] = useState(false);

  

  

  const tabs = [
    { key: "basic", label: "Basic Info", icon: <FaCheck /> },
    { key: "location", label: "Location", icon: <FaMapMarkerAlt /> },
    { key: "schedule", label: "Schedule", icon: <FaClock /> },
    { key: "media", label: "Media", icon: <FaImage /> },
    { key: "details", label: "Details", icon: <FaList /> },
    { key: "policies", label: "Policies", icon: <FaShieldAlt /> },
    { key: "client", label: "Client Info", icon: <FaUser /> },
  ];

  return (
    <>
      <Helmet>
        <title>Create Tourism Event - Admin</title>
        <meta name="description" content="Create a new tourism event with comprehensive details" />
      </Helmet>

      <div className="min-vh-100" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <Container fluid className="py-4">
          <div className="mx-auto" style={{ maxWidth: maxW }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h1 className="h2 mb-2 text-dark fw-bold" style={{
                  background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Business Onboarding
                </h1>
                <p className="text-muted mb-0 fw-medium">Design your perfect tourism experience</p>
              </div>
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate("/dashboard")}
                className="d-flex align-items-center gap-2 fw-medium"
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#cbd5e0';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <FaArrowLeft /> Back to Dashboard
              </Button>
            </div>

            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
              <Toast 
                show={showToast} 
                onClose={() => setShowToast(false)}
                delay={success ? 10000 : 5000}
                autohide
                bg={success ? "success" : "danger"}
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  border: 'none'
                }}
              >
                <Toast.Header style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <strong className="me-auto fw-semibold">
                    {success ? "Success" : "Error"}
                  </strong>
                </Toast.Header>
                <Toast.Body className={success ? "text-white" : "text-white"}>
                  <div>
                    <p className="mb-3">{success || error}</p>
                    {success && createdEventId && (
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() => navigate(`/seat-selection/${createdEventId}`)}
                          className="fw-medium"
                          style={{
                            borderRadius: '8px',
                            padding: '6px 12px',
                            fontSize: '0.875rem',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            transition: 'all 0.2s ease-in-out'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          <FaChair className="me-1" /> Setup Seating
                        </Button>
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() => setShowToast(false)}
                          className="fw-medium"
                          style={{
                            borderRadius: '8px',
                            padding: '6px 12px',
                            fontSize: '0.875rem',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            transition: 'all 0.2s ease-in-out'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                </Toast.Body>
              </Toast>
            </ToastContainer>

            {/* Main Form */}
            <Card className="border-0" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <Card.Body className="p-0">
                {/* Navigation Tabs */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: '20px 20px 0 0'
                }}>
                  <Nav variant="tabs" className="border-0 px-3">
                    {tabs.map((tab) => (
                      <Nav.Item key={tab.key}>
                        <Nav.Link
                          active={activeTab === tab.key}
                          onClick={() => setActiveTab(tab.key)}
                          className="d-flex align-items-center gap-2 py-3 fw-medium"
                          style={{
                            border: 'none',
                            borderRadius: '12px 12px 0 0',
                            marginRight: '4px',
                            transition: 'all 0.2s ease-in-out',
                            color: activeTab === tab.key ? '#667eea' : '#718096',
                            background: activeTab === tab.key ? 
                              'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' : 
                              'transparent',
                            boxShadow: activeTab === tab.key ? 
                              '0 -4px 16px rgba(0, 0, 0, 0.08)' : 
                              'none'
                          }}
                        >
                          {tab.icon} {tab.label}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                </div>

                <div className="p-4">
                  <Form onSubmit={handleSubmit}>
                    {/* Basic Information Tab */}
                    {activeTab === "basic" && (
                      <div className="space-y-4">
                        <Row>
                          <Col lg={8}>
                            <div className="mb-4">
                              <label htmlFor="title" className="form-label text-muted small mb-2 fw-medium">
                                Event Title
                              </label>
                              <Form.Control
                                type="text"
                                placeholder="Enter event title"
                                ref={eventTitleRef}
                                value={eventData.title}
                                onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                                required
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              />
                            </div>
                          </Col>
                          <Col lg={4}>
                            <div className="mb-4">
                              <label htmlFor="currency" className="form-label text-muted small mb-2 fw-medium">
                                Currency
                              </label>
                              <Form.Select
                                ref={eventCurrencyRef}
                                value={eventData.currency}
                                onChange={(e) => setEventData({ ...eventData, currency: e.target.value })}
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              >
                                <option value="CAD">CAD</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                              </Form.Select>
                            </div>
                          </Col>
                        </Row>

                        {/* Multiple Prices Section */}
                        <div className="card border-0 mb-4" style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          borderRadius: '16px',
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center gap-3 mb-4">
                              <div style={{
                                width: '48px',
                                height: '48px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)'
                              }}>
                                <FaTag style={{ color: 'white', fontSize: '20px' }} />
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold text-dark">Pricing Options</h5>
                                <p className="text-muted mb-0 small">
                                  {eventData.prices.length} price{eventData.prices.length !== 1 ? 's' : ''} configured
                                </p>
                              </div>
                            </div>

                            {/* Add New Price Form */}
                            <div className="card border-0 mb-4" style={{
                              background: 'white',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                              borderRadius: '12px',
                              border: '1px solid rgba(0, 0, 0, 0.05)'
                            }}>
                              <div className="card-body p-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <div style={{
                                    width: '32px',
                                    height: '32px',
                                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <FaPlus style={{ color: 'white', fontSize: '14px' }} />
                                  </div>
                                  <h6 className="mb-0 fw-semibold">Add New Price</h6>
                                </div>
                                <div>
                                  <Row>
                                    <Col md={3}>
                                      <div className="mb-3">
                                        <label className="form-label text-muted small mb-2 fw-medium">
                                          Price Name
                                        </label>
                                        <Form.Control
                                          type="text"
                                          placeholder="e.g., Early Bird, Regular, VIP"
                                          ref={priceNameRef}
                                          style={{
                                            height: '52px',
                                            fontSize: '0.95rem',
                                            fontWeight: '400',
                                            color: '#2d3748',
                                            backgroundColor: '#f7fafc',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '10px',
                                            transition: 'all 0.2s ease-in-out',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                          }}
                                          onFocus={(e) => {
                                            e.target.style.borderColor = '#4facfe';
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                          }}
                                          onBlur={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.backgroundColor = '#f7fafc';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                          }}
                                        />
                                      </div>
                                    </Col>
                                    <Col md={3}>
                                      <div className="mb-3">
                                        <label className="form-label text-muted small mb-2 fw-medium">
                                          Amount ({eventData.currency})
                                        </label>
                                        <Form.Control
                                          type="number"
                                          placeholder="0.00"
                                          ref={priceAmountRef}
                                          min="0"
                                          step="0.01"
                                          style={{
                                            height: '52px',
                                            fontSize: '0.95rem',
                                            fontWeight: '400',
                                            color: '#2d3748',
                                            backgroundColor: '#f7fafc',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '10px',
                                            transition: 'all 0.2s ease-in-out',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                          }}
                                          onFocus={(e) => {
                                            e.target.style.borderColor = '#4facfe';
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                          }}
                                          onBlur={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.backgroundColor = '#f7fafc';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                          }}
                                        />
                                      </div>
                                    </Col>
                                    <Col md={2}>
                                      <div className="mb-3">
                                        <label className="form-label text-muted small mb-2 fw-medium">
                                          Start Date
                                        </label>
                                        <Form.Control
                                          type="date"
                                          ref={priceStartDateRef}
                                          style={{
                                            height: '52px',
                                            fontSize: '0.95rem',
                                            fontWeight: '400',
                                            color: '#2d3748',
                                            backgroundColor: '#f7fafc',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '10px',
                                            transition: 'all 0.2s ease-in-out',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                          }}
                                          onFocus={(e) => {
                                            e.target.style.borderColor = '#4facfe';
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                          }}
                                          onBlur={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.backgroundColor = '#f7fafc';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                          }}
                                        />
                                      </div>
                                    </Col>
                                    <Col md={2}>
                                      <div className="mb-3">
                                        <label className="form-label text-muted small mb-2 fw-medium">
                                          End Date
                                        </label>
                                        <Form.Control
                                          type="date"
                                          ref={priceEndDateRef}
                                          style={{
                                            height: '52px',
                                            fontSize: '0.95rem',
                                            fontWeight: '400',
                                            color: '#2d3748',
                                            backgroundColor: '#f7fafc',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '10px',
                                            transition: 'all 0.2s ease-in-out',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                          }}
                                          onFocus={(e) => {
                                            e.target.style.borderColor = '#4facfe';
                                            e.target.style.backgroundColor = '#ffffff';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                          }}
                                          onBlur={(e) => {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.backgroundColor = '#f7fafc';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                          }}
                                        />
                                      </div>
                                    </Col>
                                    <Col md={2}>
                                      <div className="mb-3">
                                        <label className="form-label text-muted small mb-2 fw-medium">
                                          Currency
                                        </label>
                                        <Form.Select
                                          ref={priceCurrencyRef}
                                          defaultValue={eventData.currency}
                                          style={{
                                            height: '52px',
                                            fontSize: '0.95rem',
                                            fontWeight: '400',
                                            color: '#2d3748',
                                            backgroundColor: '#f7fafc',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '10px',
                                            transition: 'all 0.2s ease-in-out',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                          }}
                                        >
                                          <option value="USD">USD</option>
                                          <option value="CAD">CAD</option>
                                          <option value="EUR">EUR</option>
                                          <option value="GBP">GBP</option>
                                        </Form.Select>
                                      </div>
                                    </Col>
                                    <Col md={2}>
                                      <div className="mb-3">
                                        <label className="form-label text-muted small mb-2 fw-medium">
                                          &nbsp;
                                        </label>
                                        <Button
                                          variant="outline-primary"
                                          onClick={addPrice}
                                          className="w-100 fw-medium"
                                          style={{
                                            height: '52px',
                                            fontSize: '0.95rem',
                                            fontWeight: '500',
                                            color: '#4facfe',
                                            backgroundColor: 'white',
                                            border: '2px solid #4facfe',
                                            borderRadius: '10px',
                                            transition: 'all 0.2s ease-in-out',
                                            boxShadow: '0 2px 8px rgba(79, 172, 254, 0.15)'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#4facfe';
                                            e.target.style.color = 'white';
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.25)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'white';
                                            e.target.style.color = '#4facfe';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 2px 8px rgba(79, 172, 254, 0.15)';
                                          }}
                                        >
                                          <FaPlus className="me-2" /> Add Price
                                        </Button>
                                      </div>
                                    </Col>
                                    <div>
                                      <Row>
                                        <Col md={2}>
                                          <div className="mb-3">
                                            <label className="form-label text-muted small mb-2 fw-medium">Duration (min)</label>
                                            <Form.Control
                                              type="number"
                                              min="1"
                                              step="1"
                                              value={newPriceDuration}
                                              onChange={e => setNewPriceDuration(parseInt(e.target.value) || 60)}
                                              style={{
                                                height: '52px',
                                                fontSize: '0.95rem',
                                                fontWeight: '400',
                                                color: '#2d3748',
                                                backgroundColor: '#f7fafc',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '10px',
                                                transition: 'all 0.2s ease-in-out',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                              }}
                                            />
                                          </div>
                                        </Col>
                                        <Col md={2}>
                                          <div className="mb-3 d-flex align-items-end">
                                            <Button
                                              variant="outline-info"
                                              className="w-100"
                                              onClick={() => setShowPriceHours(v => !v)}
                                              style={{ height: '52px' }}
                                            >
                                              {showPriceHours ? 'Hide' : 'Set'} Hours
                                            </Button>
                                          </div>
                                        </Col>
                                      </Row>
                                      {showPriceHours && (
                                        <div className="mb-3 p-3 border rounded bg-light">
                                          <div className="mb-2 fw-bold">Opening Hours by Day</div>
                                          {Object.entries(newPriceOpeningHours).map(([day, val]) => (
                                            <Row key={day} className="align-items-center mb-2">
                                              <Col xs={3}><Form.Check type="checkbox" label={day} checked={val.isOpen} onChange={e => setNewPriceOpeningHours(hours => ({ ...hours, [day]: { ...hours[day], isOpen: e.target.checked } }))} /></Col>
                                              <Col xs={4}><Form.Control type="time" value={val.open} disabled={!val.isOpen} onChange={e => setNewPriceOpeningHours(hours => ({ ...hours, [day]: { ...hours[day], open: e.target.value } }))} /></Col>
                                              <Col xs={4}><Form.Control type="time" value={val.close} disabled={!val.isOpen} onChange={e => setNewPriceOpeningHours(hours => ({ ...hours, [day]: { ...hours[day], close: e.target.value } }))} /></Col>
                                            </Row>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </Row>
                                </div>
                              </div>
                            </div>

                            {/* Existing Prices List */}
                            <div className="card border-0" style={{
                              background: 'white',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                              borderRadius: '12px',
                              border: '1px solid rgba(0, 0, 0, 0.05)'
                            }}>
                              <div className="card-body p-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <div style={{
                                    width: '32px',
                                    height: '32px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <FaList style={{ color: 'white', fontSize: '14px' }} />
                                  </div>
                                  <h6 className="mb-0 fw-semibold">Current Prices</h6>
                                </div>
                                {eventData.prices.length === 0 ? (
                                  <div className="text-center py-5">
                                    <div style={{
                                      width: '64px',
                                      height: '64px',
                                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                      borderRadius: '16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: '0 auto 16px',
                                      boxShadow: '0 8px 24px rgba(240, 147, 251, 0.3)'
                                    }}>
                                      <FaTag style={{ color: 'white', fontSize: '24px' }} />
                                    </div>
                                    <h6 className="text-muted mb-2">No prices added yet</h6>
                                    <p className="text-muted small mb-0">Add your first pricing option above</p>
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    {eventData.prices.map((price, index) => (
                                      <div key={price.id} className="card border-0" style={{
                                        background: price.isActive ? 
                                          'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' : 
                                          'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                        boxShadow: price.isActive ? 
                                          '0 4px 16px rgba(0, 0, 0, 0.06)' : 
                                          '0 2px 8px rgba(0, 0, 0, 0.04)',
                                        borderRadius: '12px',
                                        border: price.isActive ? 
                                          '1px solid rgba(102, 126, 234, 0.1)' : 
                                          '1px solid rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.2s ease-in-out'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (price.isActive) {
                                          e.currentTarget.style.transform = 'translateY(-2px)';
                                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (price.isActive) {
                                          e.currentTarget.style.transform = 'translateY(0)';
                                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.06)';
                                        }
                                      }}>
                                        <div className="card-body p-3">
                                          <Row className="align-items-center">
                                            <Col md={3}>
                                              <div className="d-flex align-items-center">
                                                <Form.Check
                                                  type="checkbox"
                                                  checked={price.isActive}
                                                  onChange={() => togglePriceActive(price.id)}
                                                  className="me-3"
                                                  style={{
                                                    transform: 'scale(1.2)',
                                                    accentColor: '#667eea'
                                                  }}
                                                />
                                                <div>
                                                  <h6 className="mb-1 fw-semibold" style={{
                                                    color: price.isActive ? '#2d3748' : '#718096'
                                                  }}>
                                                    {price.name}
                                                  </h6>
                                                  {!price.isActive && (
                                                    <Badge style={{
                                                      background: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)',
                                                      border: 'none',
                                                      fontSize: '0.7rem',
                                                      padding: '4px 8px',
                                                      borderRadius: '6px'
                                                    }}>
                                                      Inactive
                                                    </Badge>
                                                  )}
                                                </div>
                                              </div>
                                            </Col>
                                            <Col md={2}>
                                              <div className="text-center">
                                                <span className="fw-bold fs-5" style={{
                                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                  WebkitBackgroundClip: 'text',
                                                  WebkitTextFillColor: 'transparent',
                                                  backgroundClip: 'text'
                                                }}>
                                                  {price.currency || eventData.currency} {price.amount.toFixed(2)}
                                                </span>
                                              </div>
                                            </Col>
                                            <Col md={4}>
                                              <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                  width: '8px',
                                                  height: '8px',
                                                  background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                                                  borderRadius: '50%',
                                                  boxShadow: '0 0 8px rgba(72, 187, 120, 0.4)'
                                                }}></div>
                                                <small className="text-muted fw-medium">
                                                  {price.startDate instanceof Date ? 
                                                    price.startDate.toLocaleDateString() : 
                                                    new Date(price.startDate).toLocaleDateString()
                                                  } - {price.endDate instanceof Date ? 
                                                    price.endDate.toLocaleDateString() : 
                                                    new Date(price.endDate).toLocaleDateString()
                                                  }
                                                </small>
                                              </div>
                                            </Col>
                                            <Col md={3}>
                                              <div className="d-flex justify-content-end gap-2">
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  onClick={() => removePrice(price.id)}
                                                  disabled={eventData.prices.length <= 1}
                                                  style={{
                                                    border: '2px solid #f56565',
                                                    borderRadius: '8px',
                                                    padding: '6px 12px',
                                                    transition: 'all 0.2s ease-in-out',
                                                    opacity: eventData.prices.length <= 1 ? 0.5 : 1
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (eventData.prices.length > 1) {
                                                      e.target.style.backgroundColor = '#f56565';
                                                      e.target.style.color = 'white';
                                                      e.target.style.transform = 'scale(1.05)';
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (eventData.prices.length > 1) {
                                                      e.target.style.backgroundColor = 'transparent';
                                                      e.target.style.color = '#f56565';
                                                      e.target.style.transform = 'scale(1)';
                                                    }
                                                  }}
                                                >
                                                  <FaTrash style={{ fontSize: '12px' }} />
                                                </Button>
                                              </div>
                                            </Col>
                                          </Row>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <Row>
                          <Col md={6}>
                            <div className="mb-4">
                              <label htmlFor="capacity" className="form-label text-muted small mb-2 fw-medium">
                                Capacity
                              </label>
                              <Form.Control
                                type="number"
                                placeholder="50"
                                ref={eventCapacityRef}
                                value={eventData.capacity}
                                onChange={(e) => setEventData({ ...eventData, capacity: parseInt(e.target.value) || 0 })}
                                min="1"
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <label htmlFor="duration" className="form-label text-muted small mb-2 fw-medium">
                                Duration (hours)
                              </label>
                              <Form.Control
                                type="number"
                                placeholder="2"
                                ref={eventDurationRef}
                                value={eventData.duration}
                                onChange={(e) => setEventData({ ...eventData, duration: parseInt(e.target.value) || 0 })}
                                min="0.5"
                                step="0.5"
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              />
                            </div>
                          </Col>
                        </Row>

                        <div className="mb-4">
                          <label htmlFor="shortDescription" className="form-label text-muted small mb-2 fw-medium">
                            Short Description
                          </label>
                          <Form.Control
                            as="textarea"
                            placeholder="Brief description (max 200 characters)"
                            ref={eventShortDescriptionRef}
                            value={eventData.shortDescription}
                            onChange={(e) => setEventData({ ...eventData, shortDescription: e.target.value })}
                            maxLength={200}
                            rows={2}
                            style={{
                              fontSize: '1rem',
                              fontWeight: '400',
                              color: '#2d3748',
                              backgroundColor: '#f7fafc',
                              border: '2px solid #e2e8f0',
                              borderRadius: '12px',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
                              resize: 'none'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#4facfe';
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e2e8f0';
                              e.target.style.backgroundColor = '#f7fafc';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                            }}
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="description" className="form-label text-muted small mb-2 fw-medium">
                            Full Description
                          </label>
                          <Form.Control
                            as="textarea"
                            placeholder="Detailed description of the event"
                            ref={eventDescriptionRef}
                            value={eventData.description}
                            onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                            rows={5}
                            required
                            style={{
                              fontSize: '1rem',
                              fontWeight: '400',
                              color: '#2d3748',
                              backgroundColor: '#f7fafc',
                              border: '2px solid #e2e8f0',
                              borderRadius: '12px',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
                              resize: 'none'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#4facfe';
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e2e8f0';
                              e.target.style.backgroundColor = '#f7fafc';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                            }}
                          />
                        </div>

                        <Row>
                          <Col md={6}>
                            <div className="mb-4">
                              <label htmlFor="difficulty" className="form-label text-muted small mb-2 fw-medium">
                                Difficulty Level
                              </label>
                              <Form.Select
                                ref={eventDifficultyRef}
                                value={eventData.difficulty}
                                onChange={(e) => setEventData({ ...eventData, difficulty: e.target.value })}
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              >
                                <option value="Easy">Easy</option>
                                <option value="Moderate">Moderate</option>
                                <option value="Difficult">Difficult</option>
                                <option value="Expert">Expert</option>
                              </Form.Select>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <label htmlFor="ageMin" className="form-label text-muted small mb-2 fw-medium">
                                Minimum Age
                              </label>
                              <Form.Control
                                type="number"
                                placeholder="0"
                                ref={eventAgeMinRef}
                                value={eventData.ageMin}
                                onChange={(e) => setEventData({ ...eventData, ageMin: parseInt(e.target.value) || 0 })}
                                min="0"
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <div className="mb-4">
                              <label htmlFor="ageMax" className="form-label text-muted small mb-2 fw-medium">
                                Maximum Age
                              </label>
                              <Form.Control
                                type="number"
                                placeholder="100"
                                ref={eventAgeMaxRef}
                                value={eventData.ageMax}
                                onChange={(e) => setEventData({ ...eventData, ageMax: parseInt(e.target.value) || 100 })}
                                min="0"
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Location Tab */}
                    {activeTab === "location" && (
                      <div className="space-y-4">
                        <div className="card border-0 mb-4" style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          borderRadius: '16px',
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center gap-3 mb-4">
                              <div style={{
                                width: '48px',
                                height: '48px',
                                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 16px rgba(72, 187, 120, 0.3)'
                              }}>
                                <FaMapMarkerAlt style={{ color: 'white', fontSize: '20px' }} />
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold text-dark">Location Details</h5>
                                <p className="text-muted mb-0 small">Set your event location and contact information</p>
                              </div>
                            </div>

                            <Row>
                              <Col md={6}>
                                <div className="mb-4">
                                  <label htmlFor="location" className="form-label text-muted small mb-2 fw-medium">
                                    Location Name
                                  </label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter location name"
                                    ref={eventLocationRef}
                                    value={eventData.location}
                                    onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                                    style={{
                                      height: '56px',
                                      fontSize: '1rem',
                                      fontWeight: '400',
                                      color: '#2d3748',
                                      backgroundColor: '#f7fafc',
                                      border: '2px solid #e2e8f0',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease-in-out',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#4facfe';
                                      e.target.style.backgroundColor = '#ffffff';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#e2e8f0';
                                      e.target.style.backgroundColor = '#f7fafc';
                                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                    }}
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-4">
                                  <label htmlFor="address" className="form-label text-muted small mb-2 fw-medium">
                                    Street Address
                                  </label>
                                  {process.env.REACT_APP_GOOGLE_API ? (
                                    <Autocomplete
                                      apiKey={process.env.REACT_APP_GOOGLE_API}
                                      onPlaceSelected={(place) => {
                                        console.log('Place selected:', place);
                                        const addressComponents = place.address_components || [];
                                        let streetNumber = '';
                                        let route = '';
                                        let city = '';
                                        let state = '';
                                        let country = '';
                                        let postalCode = '';

                                        addressComponents.forEach((component) => {
                                          const types = component.types;
                                          if (types.includes('street_number')) {
                                            streetNumber = component.long_name;
                                          }
                                          if (types.includes('route')) {
                                            route = component.long_name;
                                          }
                                          if (types.includes('locality')) {
                                            city = component.long_name;
                                          }
                                          if (types.includes('administrative_area_level_1')) {
                                            state = component.long_name;
                                          }
                                          if (types.includes('country')) {
                                            country = component.long_name;
                                          }
                                          if (types.includes('postal_code')) {
                                            postalCode = component.long_name;
                                          }
                                        });

                                        setEventData({
                                          ...eventData,
                                          address: place.formatted_address || '',
                                          city: city,
                                          province: state,
                                          country: country,
                                          postalCode: postalCode,
                                        });
                                      }}
                                      options={{
                                        types: ['address'],
                                        componentRestrictions: { country: ['ca', 'us'] }
                                      }}
                                      className="form-control"
                                      placeholder="Start typing an address..."
                                      defaultValue={eventData.address}
                                      onBlur={(e) => {
                                        setEventData({ ...eventData, address: e.target.value });
                                      }}
                                      style={{
                                        height: '56px',
                                        fontSize: '1rem',
                                        fontWeight: '400',
                                        color: '#2d3748',
                                        backgroundColor: '#f7fafc',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '12px',
                                        transition: 'all 0.2s ease-in-out',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                      }}
                                    />
                                  ) : (
                                    <Form.Control
                                      type="text"
                                      placeholder="Enter street address"
                                      value={eventData.address}
                                      onChange={(e) => setEventData({ ...eventData, address: e.target.value })}
                                      style={{
                                        height: '56px',
                                        fontSize: '1rem',
                                        fontWeight: '400',
                                        color: '#2d3748',
                                        backgroundColor: '#f7fafc',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '12px',
                                        transition: 'all 0.2s ease-in-out',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = '#4facfe';
                                        e.target.style.backgroundColor = '#ffffff';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = '#e2e8f0';
                                        e.target.style.backgroundColor = '#f7fafc';
                                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                      }}
                                    />
                                  )}
                                  {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                                    <small className="text-muted">
                                      Google Maps API key not configured. Address autocomplete disabled.
                                    </small>
                                  )}
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col md={4}>
                                <div className="mb-4">
                                  <label htmlFor="city" className="form-label text-muted small mb-2 fw-medium">
                                    City
                                  </label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter city"
                                    ref={eventCityRef}
                                    value={eventData.city}
                                    onChange={(e) => setEventData({ ...eventData, city: e.target.value })}
                                    style={{
                                      height: '56px',
                                      fontSize: '1rem',
                                      fontWeight: '400',
                                      color: '#2d3748',
                                      backgroundColor: '#f7fafc',
                                      border: '2px solid #e2e8f0',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease-in-out',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#4facfe';
                                      e.target.style.backgroundColor = '#ffffff';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#e2e8f0';
                                      e.target.style.backgroundColor = '#f7fafc';
                                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                    }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-4">
                                  <label htmlFor="province" className="form-label text-muted small mb-2 fw-medium">
                                    Province/State
                                  </label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter province/state"
                                    ref={eventProvinceRef}
                                    value={eventData.province}
                                    onChange={(e) => setEventData({ ...eventData, province: e.target.value })}
                                    style={{
                                      height: '56px',
                                      fontSize: '1rem',
                                      fontWeight: '400',
                                      color: '#2d3748',
                                      backgroundColor: '#f7fafc',
                                      border: '2px solid #e2e8f0',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease-in-out',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#4facfe';
                                      e.target.style.backgroundColor = '#ffffff';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#e2e8f0';
                                      e.target.style.backgroundColor = '#f7fafc';
                                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                    }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-4">
                                  <label htmlFor="country" className="form-label text-muted small mb-2 fw-medium">
                                    Country
                                  </label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter country"
                                    ref={eventCountryRef}
                                    value={eventData.country}
                                    onChange={(e) => setEventData({ ...eventData, country: e.target.value })}
                                    style={{
                                      height: '56px',
                                      fontSize: '1rem',
                                      fontWeight: '400',
                                      color: '#2d3748',
                                      backgroundColor: '#f7fafc',
                                      border: '2px solid #e2e8f0',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease-in-out',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#4facfe';
                                      e.target.style.backgroundColor = '#ffffff';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#e2e8f0';
                                      e.target.style.backgroundColor = '#f7fafc';
                                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                    }}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col md={4}>
                                <div className="mb-4">
                                  <label htmlFor="postalCode" className="form-label text-muted small mb-2 fw-medium">
                                    Postal Code
                                  </label>
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter postal code"
                                    ref={eventPostalCodeRef}
                                    value={eventData.postalCode}
                                    onChange={(e) => setEventData({ ...eventData, postalCode: e.target.value })}
                                    style={{
                                      height: '56px',
                                      fontSize: '1rem',
                                      fontWeight: '400',
                                      color: '#2d3748',
                                      backgroundColor: '#f7fafc',
                                      border: '2px solid #e2e8f0',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease-in-out',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#4facfe';
                                      e.target.style.backgroundColor = '#ffffff';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#e2e8f0';
                                      e.target.style.backgroundColor = '#f7fafc';
                                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                    }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-4">
                                  <label htmlFor="phone" className="form-label text-muted small mb-2 fw-medium">
                                    Phone Number
                                  </label>
                                  <Form.Control
                                    type="tel"
                                    placeholder="Enter phone number"
                                    ref={eventPhoneRef}
                                    value={eventData.phone}
                                    onChange={(e) => setEventData({ ...eventData, phone: e.target.value })}
                                    style={{
                                      height: '56px',
                                      fontSize: '1rem',
                                      fontWeight: '400',
                                      color: '#2d3748',
                                      backgroundColor: '#f7fafc',
                                      border: '2px solid #e2e8f0',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease-in-out',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#4facfe';
                                      e.target.style.backgroundColor = '#ffffff';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#e2e8f0';
                                      e.target.style.backgroundColor = '#f7fafc';
                                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                    }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-4">
                                  <label htmlFor="email" className="form-label text-muted small mb-2 fw-medium">
                                    Email
                                  </label>
                                  <Form.Control
                                    type="email"
                                    placeholder="Enter email"
                                    ref={eventEmailRef}
                                    value={eventData.email}
                                    onChange={(e) => setEventData({ ...eventData, email: e.target.value })}
                                    style={{
                                      height: '56px',
                                      fontSize: '1rem',
                                      fontWeight: '400',
                                      color: '#2d3748',
                                      backgroundColor: '#f7fafc',
                                      border: '2px solid #e2e8f0',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease-in-out',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#4facfe';
                                      e.target.style.backgroundColor = '#ffffff';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#e2e8f0';
                                      e.target.style.backgroundColor = '#f7fafc';
                                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                    }}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <div className="mb-4">
                              <label htmlFor="website" className="form-label text-muted small mb-2 fw-medium">
                                Website
                              </label>
                              <Form.Control
                                type="url"
                                placeholder="https://example.com"
                                ref={eventWebsiteRef}
                                value={eventData.website}
                                onChange={(e) => setEventData({ ...eventData, website: e.target.value })}
                                style={{
                                  height: '56px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  color: '#2d3748',
                                  backgroundColor: '#f7fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#4facfe';
                                  e.target.style.backgroundColor = '#ffffff';
                                  e.target.style.boxShadow = '0 0 0 3px rgba(79, 172, 254, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.backgroundColor = '#f7fafc';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.02)';
                                }}
                              />
                            </div>

                            {/* Address Preview */}
                            {eventData.address && (
                              <div className="card border-0" style={{
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                                borderRadius: '12px',
                                border: '1px solid rgba(72, 187, 120, 0.1)'
                              }}>
                                <div className="card-body p-3">
                                  <h6 className="card-title d-flex align-items-center gap-2 mb-2">
                                    <div style={{
                                      width: '24px',
                                      height: '24px',
                                      background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                                      borderRadius: '6px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <FaMapMarkerAlt style={{ color: 'white', fontSize: '12px' }} />
                                    </div>
                                    Address Preview
                                  </h6>
                                  <p className="mb-1 fw-medium">{eventData.address}</p>
                                  <p className="mb-0 text-muted small">
                                    {eventData.city && `${eventData.city}, `}
                                    {eventData.province && `${eventData.province} `}
                                    {eventData.postalCode && `${eventData.postalCode}`}
                                    {eventData.country && `, ${eventData.country}`}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Google Maps API Status */}
                            <div className="card border-0" style={{
                              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                              borderRadius: '12px',
                              border: '1px solid rgba(0, 0, 0, 0.05)'
                            }}>
                              <div className="card-body p-3">
                                <h6 className="card-title d-flex align-items-center gap-2 mb-2">
                                  <div style={{
                                    width: '24px',
                                    height: '24px',
                                    background: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 
                                      'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' :
                                      'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <FaMapMarkerAlt style={{ color: 'white', fontSize: '12px' }} />
                                  </div>
                                  Google Maps API Status
                                </h6>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="mb-1">
                                      <strong>Status:</strong> {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 
                                        <span className="text-success fw-medium">API Key Configured</span> : 
                                        <span className="text-warning fw-medium">API Key Missing</span>
                                      }
                                    </p>
                                    <p className="mb-0 text-muted small">
                                      {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 
                                        'Address autocomplete is enabled. Start typing in the address field above.' :
                                        'To enable address autocomplete, add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.'
                                      }
                                    </p>
                                  </div>
                                  {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                                    <Button 
                                      variant="outline-primary" 
                                      size="sm"
                                      style={{
                                        border: '2px solid #4facfe',
                                        borderRadius: '8px',
                                        padding: '6px 12px',
                                        transition: 'all 0.2s ease-in-out'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#4facfe';
                                        e.target.style.color = 'white';
                                        e.target.style.transform = 'scale(1.05)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.color = '#4facfe';
                                        e.target.style.transform = 'scale(1)';
                                      }}
                                      onClick={() => {
                                        console.log('Testing Google Maps API...');
                                        const testInput = document.querySelector('input[placeholder="Start typing an address..."]');
                                        if (testInput) {
                                          testInput.focus();
                                          console.log('Address input focused. Try typing an address.');
                                        }
                                      }}
                                    >
                                      Test Autocomplete
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Schedule Tab */}
                    {activeTab === "schedule" && (
                      <div className="space-y-4">
                        <div className="mb-4">
                          <h5 className="mb-3">Opening Hours</h5>
                          <p className="text-muted">Set opening hours for each day of the week</p>
                          
                          {openingHours.map((hour, index) => (
                            <div key={hour.day} className="card mb-3 border-0 bg-light">
                              <div className="card-body">
                                <Row className="align-items-center">
                                  <Col md={2}>
                                    <Form.Check
                                      type="checkbox"
                                      label={hour.day}
                                      checked={hour.isOpen}
                                      onChange={(e) => handleOpeningHoursChange(index, "isOpen", e.target.checked)}
                                      className="fw-bold"
                                    />
                                  </Col>
                                  <Col md={5}>
                                    <Form.Control
                                      type="time"
                                      value={hour.open}
                                      onChange={(e) => handleOpeningHoursChange(index, "open", e.target.value)}
                                      disabled={!hour.isOpen}
                                      style={{
                                        height: '58px',
                                        fontSize: '1rem',
                                        fontWeight: '400',
                                        lineHeight: '1.5',
                                        color: '#212529',
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                      }}
                                    />
                                  </Col>
                                  <Col md={5}>
                                    <Form.Control
                                      type="time"
                                      value={hour.close}
                                      onChange={(e) => handleOpeningHoursChange(index, "close", e.target.value)}
                                      disabled={!hour.isOpen}
                                      style={{
                                        height: '58px',
                                        fontSize: '1rem',
                                        fontWeight: '400',
                                        lineHeight: '1.5',
                                        color: '#212529',
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                      }}
                                    />
                                  </Col>
                                </Row>
                                {/* Show time slots for this day if open */}
                                {hour.isOpen && (
                                  <div className="mt-2">
                                    <span className="text-muted small">Time Slots: </span>
                                    {generateTimeSlots(hour.open, hour.close, sessionLength).length === 0 ? (
                                      <span className="text-danger small">No slots (check hours or session length)</span>
                                    ) : (
                                      <div className="d-flex flex-wrap gap-2 mt-1">
                                        {generateTimeSlots(hour.open, hour.close, sessionLength).map((slot, i) => (
                                          <Badge bg="info" key={i}>{slot}</Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mb-4">
                          <label className="form-label text-muted small mb-2 fw-medium">Session Length (minutes)</label>
                          <Form.Control
                            type="number"
                            min="5"
                            step="5"
                            value={sessionLength}
                            onChange={e => setSessionLength(parseInt(e.target.value) || 60)}
                            style={{
                              width: '200px',
                              height: '48px',
                              fontSize: '1rem',
                              fontWeight: '400',
                              color: '#2d3748',
                              backgroundColor: '#f7fafc',
                              border: '2px solid #e2e8f0',
                              borderRadius: '10px',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Media Tab */}
                    {activeTab === "media" && (
                      <div className="space-y-4">
                        <Row>
                          <Col md={6}>
                            <div className="card border-0 bg-light h-100">
                              <div className="card-body">
                                <h6 className="card-title d-flex align-items-center gap-2">
                                  <FaImage /> Upload Images
                                </h6>
                                <p className="text-muted small mb-2">
                                  Maximum 4 photos allowed ({selectedImages.length}/4)
                                </p>
                                <Form.Control
                                  ref={fileInputRef}
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={handleImageSelect}
                                  className="mb-3"
                                  disabled={selectedImages.length >= 4}
                                  style={{
                                    height: '58px',
                                    fontSize: '1rem',
                                    fontWeight: '400',
                                    lineHeight: '1.5',
                                    color: '#212529',
                                    backgroundColor: selectedImages.length >= 4 ? '#f8f9fa' : '#fff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                  }}
                                />
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => fileInputRef.current?.click()}
                                  className="mb-2"
                                  disabled={selectedImages.length >= 4}
                                >
                                  <FaImage /> Select Images Manually
                                </Button>
                                {selectedImages.length > 0 && (
                                  <div className="mb-3">
                                    <Badge bg={selectedImages.length >= 4 ? "success" : "info"} className="me-2">
                                      {selectedImages.length}/4 image(s) selected
                                    </Badge>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => setSelectedImages([])}
                                    >
                                      <FaTimes /> Clear
                                    </Button>
                                  </div>
                                )}
                                
                                {uploadingImages && (
                                  <div className="mb-3">
                                    <ProgressBar 
                                      now={imageUploadProgress} 
                                      label={`${Math.round(imageUploadProgress)}%`}
                                      className="mb-2"
                                    />
                                    <small className="text-muted">Uploading images...</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="card border-0 bg-light h-100">
                              <div className="card-body">
                                <h6 className="card-title d-flex align-items-center gap-2">
                                  <FaMicrophone /> Audio Guide
                                </h6>
                                <Button
                                  variant="outline-primary"
                                  onClick={() => setShowAudioModal(true)}
                                  className="mb-3 w-100"
                                  style={{
                                    height: '58px',
                                    fontSize: '1rem',
                                    fontWeight: '400',
                                    lineHeight: '1.5',
                                    color: '#212529',
                                    backgroundColor: '#fff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                  }}
                                >
                                  {eventData.audioGuide ? "Replace Audio Guide" : "Add Audio Guide"}
                                </Button>
                                {eventData.audioGuide && (
                                  <div>
                                    <ReactAudioPlayer
                                      src={eventData.audioGuide}
                                      controls
                                      className="w-100"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Photo Preview Section */}
                        {selectedImages.length > 0 && (
                          <div className="card border-0 bg-light">
                            <div className="card-body">
                              <h6 className="card-title d-flex align-items-center gap-2">
                                <FaEye /> Photo Previews ({selectedImages.length})
                              </h6>
                              <p className="text-muted small mb-3">
                                Preview of selected images before upload
                              </p>
                              <Row>
                                {selectedImages.map((image, index) => (
                                  <Col key={index} md={3} sm={4} xs={6} className="mb-3">
                                    <div className="position-relative">
                                      <img
                                        src={URL.createObjectURL(image)}
                                        alt={`Preview ${index + 1}`}
                                        className="img-fluid rounded"
                                        style={{
                                          width: '100%',
                                          height: '150px',
                                          objectFit: 'cover',
                                          border: '2px solid #dee2e6'
                                        }}
                                      />
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        className="position-absolute top-0 end-0 m-1"
                                        style={{ width: '24px', height: '24px', padding: '0' }}
                                        onClick={() => {
                                          const newImages = selectedImages.filter((_, i) => i !== index);
                                          setSelectedImages(newImages);
                                        }}
                                      >
                                        <FaTimes size={10} />
                                      </Button>
                                      <div className="position-absolute bottom-0 start-0 w-100 p-2" 
                                           style={{ 
                                             background: 'rgba(0,0,0,0.7)', 
                                             color: 'white',
                                             fontSize: '0.75rem'
                                           }}>
                                        {image.name.length > 20 ? 
                                          image.name.substring(0, 20) + '...' : 
                                          image.name
                                        }
                                      </div>
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          </div>
                        )}

                        {/* Uploaded Photos List */}
                        {eventData.images && eventData.images.length > 0 && (
                          <div className="card border-0 bg-light">
                            <div className="card-body">
                              <h6 className="card-title d-flex align-items-center gap-2">
                                <FaImages /> Uploaded Photos ({eventData.images.length})
                              </h6>
                              <p className="text-muted small mb-3">
                                Photos that have been uploaded to the event
                              </p>
                              <Row>
                                {eventData.images.map((imageUrl, index) => (
                                  <Col key={index} md={3} sm={4} xs={6} className="mb-3">
                                    <div className="position-relative">
                                      <img
                                        src={imageUrl}
                                        alt={`Uploaded ${index + 1}`}
                                        className="img-fluid rounded"
                                        style={{
                                          width: '100%',
                                          height: '150px',
                                          objectFit: 'cover',
                                          border: '2px solid #dee2e6'
                                        }}
                                      />
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        className="position-absolute top-0 end-0 m-1"
                                        style={{ width: '24px', height: '24px', padding: '0' }}
                                        onClick={() => {
                                          const newImages = eventData.images.filter((_, i) => i !== index);
                                          setEventData({ ...eventData, images: newImages });
                                        }}
                                      >
                                        <FaTimes size={10} />
                                      </Button>
                                      <div className="position-absolute bottom-0 start-0 w-100 p-2" 
                                           style={{ 
                                             background: 'rgba(0,0,0,0.7)', 
                                             color: 'white',
                                             fontSize: '0.75rem'
                                           }}>
                                        Photo {index + 1}
                                      </div>
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          </div>
                        )}

                        <div className="mb-3">
                          <label htmlFor="videoURL" className="form-label text-muted small mb-2">
                            Video URL (YouTube/Vimeo)
                          </label>
                          <Form.Control
                            type="url"
                            placeholder="https://www.youtube.com/watch?v=..."
                            ref={eventVideoURLRef}
                            value={eventData.videoURL}
                            onChange={(e) => setEventData({ ...eventData, videoURL: e.target.value })}
                            style={{
                              height: '58px',
                              fontSize: '1rem',
                              fontWeight: '400',
                              lineHeight: '1.5',
                              color: '#212529',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                            }}
                          />
                        </div>

                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title d-flex align-items-center gap-2">
                              <FaTag /> Tags
                            </h6>
                            <Row>
                              <Col md={8}>
                                <Form.Control
                                  type="text"
                                  placeholder="Add a tag"
                                  ref={eventTagsRef}
                                  style={{
                                    height: '58px',
                                    fontSize: '1rem',
                                    fontWeight: '400',
                                    lineHeight: '1.5',
                                    color: '#212529',
                                    backgroundColor: '#fff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                  }}
                                />
                              </Col>
                              <Col md={4}>
                                <Button variant="outline-primary" onClick={addTag} className="w-100">
                                  <FaPlus /> Add Tag
                                </Button>
                              </Col>
                            </Row>
                            <div className="mt-3">
                              {eventData.tags.map((tag, index) => (
                                <Badge key={index} bg="primary" className="me-2 mb-2 p-2">
                                  {tag}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-white p-0 ms-2"
                                    onClick={() => removeTag(tag)}
                                  >
                                    <FaTimes />
                                  </Button>
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Details Tab */}
                    {activeTab === "details" && (
                      <div className="space-y-4">
                        <Row>
                          <Col md={6}>
                            <div className="card border-0 bg-light h-100">
                              <div className="card-body">
                                <h6 className="card-title text-success">What's Included</h6>
                                <Row>
                                  <Col md={8}>
                                    <Form.Control
                                      type="text"
                                      placeholder="Add included item"
                                      ref={eventIncludedRef}
                                      style={{
                                        height: '58px',
                                        fontSize: '1rem',
                                        fontWeight: '400',
                                        lineHeight: '1.5',
                                        color: '#212529',
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                      }}
                                    />
                                  </Col>
                                  <Col md={4}>
                                    <Button variant="outline-success" onClick={addIncludedItem} className="w-100">
                                      <FaPlus /> Add
                                    </Button>
                                  </Col>
                                </Row>
                                <div className="mt-3">
                                  {eventData.included.map((item, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-white rounded">
                                      <span>{item}</span>
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        onClick={() => removeIncludedItem(item)}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="card border-0 bg-light h-100">
                              <div className="card-body">
                                <h6 className="card-title text-warning">What's Not Included</h6>
                                <Row>
                                  <Col md={8}>
                                    <Form.Control
                                      type="text"
                                      placeholder="Add not included item"
                                      ref={eventNotIncludedRef}
                                      style={{
                                        height: '58px',
                                        fontSize: '1rem',
                                        fontWeight: '400',
                                        lineHeight: '1.5',
                                        color: '#212529',
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                      }}
                                    />
                                  </Col>
                                  <Col md={4}>
                                    <Button variant="outline-warning" onClick={addNotIncludedItem} className="w-100">
                                      <FaPlus /> Add
                                    </Button>
                                  </Col>
                                </Row>
                                <div className="mt-3">
                                  {eventData.notIncluded.map((item, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-white rounded">
                                      <span>{item}</span>
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        onClick={() => removeNotIncludedItem(item)}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-info">What to Bring</h6>
                            <Row>
                              <Col md={8}>
                                <Form.Control
                                  type="text"
                                  placeholder="Add item to bring"
                                  ref={eventWhatToBringRef}
                                  style={{
                                    height: '58px',
                                    fontSize: '1rem',
                                    fontWeight: '400',
                                    lineHeight: '1.5',
                                    color: '#212529',
                                    backgroundColor: '#fff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                  }}
                                />
                              </Col>
                              <Col md={4}>
                                <Button variant="outline-info" onClick={addWhatToBringItem} className="w-100">
                                  <FaPlus /> Add
                                </Button>
                              </Col>
                            </Row>
                            <div className="mt-3">
                              {eventData.whatToBring.map((item, index) => (
                                <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-white rounded">
                                  <span>{item}</span>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-danger p-0"
                                    onClick={() => removeWhatToBringItem(item)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Policies Tab */}
                    {activeTab === "policies" && (
                      <div className="space-y-4">
                        <div className="mb-3">
                          <label htmlFor="cancellationPolicy" className="form-label text-muted small mb-2">
                            Cancellation Policy
                          </label>
                          <Form.Control
                            as="textarea"
                            placeholder="Describe your cancellation policy"
                            ref={eventCancellationPolicyRef}
                            value={eventData.cancellationPolicy}
                            onChange={(e) => setEventData({ ...eventData, cancellationPolicy: e.target.value })}
                            rows={3}
                            style={{
                              fontSize: '1rem',
                              fontWeight: '400',
                              lineHeight: '1.5',
                              color: '#212529',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                            }}
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="refundPolicy" className="form-label text-muted small mb-2">
                            Refund Policy
                          </label>
                          <Form.Control
                            as="textarea"
                            placeholder="Describe your refund policy"
                            ref={eventRefundPolicyRef}
                            value={eventData.refundPolicy}
                            onChange={(e) => setEventData({ ...eventData, refundPolicy: e.target.value })}
                            rows={3}
                            style={{
                              fontSize: '1rem',
                              fontWeight: '400',
                              lineHeight: '1.5',
                              color: '#212529',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                            }}
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="termsConditions" className="form-label text-muted small mb-2">
                            Terms & Conditions
                          </label>
                          <Form.Control
                            as="textarea"
                            placeholder="Enter terms and conditions"
                            ref={eventTermsConditionsRef}
                            value={eventData.termsConditions}
                            onChange={(e) => setEventData({ ...eventData, termsConditions: e.target.value })}
                            rows={4}
                            style={{
                              fontSize: '1rem',
                              fontWeight: '400',
                              lineHeight: '1.5',
                              color: '#212529',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                            }}
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="accessibility" className="form-label text-muted small mb-2">
                            Accessibility Information
                          </label>
                          <Form.Control
                            as="textarea"
                            placeholder="Describe accessibility features and limitations"
                            ref={eventAccessibilityRef}
                            value={eventData.accessibility}
                            onChange={(e) => setEventData({ ...eventData, accessibility: e.target.value })}
                            rows={3}
                            style={{
                              fontSize: '1rem',
                              fontWeight: '400',
                              lineHeight: '1.5',
                              color: '#212529',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Client Information Tab */}
                    {activeTab === "client" && (
                      <div className="space-y-4">
                        <Row>
                          <Col md={8}>
                            <div className="mb-3">
                              <label htmlFor="clientName" className="form-label text-muted small mb-2">
                                Client Name
                              </label>
                              <Form.Control
                                type="text"
                                placeholder="Enter client name"
                                ref={eventClientNameRef}
                                value={eventData.clientName}
                                onChange={(e) => setEventData({ ...eventData, clientName: e.target.value })}
                                style={{
                                  height: '58px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  lineHeight: '1.5',
                                  color: '#212529',
                                  backgroundColor: '#fff',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <label htmlFor="clientPhone" className="form-label text-muted small mb-2">
                                Client Phone
                              </label>
                              <Form.Control
                                type="tel"
                                placeholder="Enter phone number"
                                ref={eventClientPhoneRef}
                                value={eventData.clientPhone}
                                onChange={(e) => setEventData({ ...eventData, clientPhone: e.target.value })}
                                style={{
                                  height: '58px',
                                  fontSize: '1rem',
                                  fontWeight: '400',
                                  lineHeight: '1.5',
                                  color: '#212529',
                                  backgroundColor: '#fff',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                }}
                              />
                            </div>
                          </Col>
                        </Row>

                        <div className="mb-3">
                          <label htmlFor="clientEmail" className="form-label text-muted small mb-2">
                            Client Email
                          </label>
                          <Form.Control
                            type="email"
                            placeholder="Enter client email"
                            ref={eventClientEmailRef}
                            value={eventData.clientEmail}
                            onChange={(e) => setEventData({ ...eventData, clientEmail: e.target.value })}
                            style={{
                              height: '58px',
                              fontSize: '1rem',
                              fontWeight: '400',
                              lineHeight: '1.5',
                              color: '#212529',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                            }}
                          />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="clientDescription" className="form-label text-muted small mb-2">
                            Client Description
                          </label>
                          <Form.Control
                            as="textarea"
                            placeholder="Describe the client or their requirements"
                            ref={eventClientDescriptionRef}
                            value={eventData.clientDescription}
                            onChange={(e) => setEventData({ ...eventData, clientDescription: e.target.value })}
                            rows={4}
                            style={{
                              fontSize: '1rem',
                              fontWeight: '400',
                              lineHeight: '1.5',
                              color: '#212529',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                            }}
                          />
                        </div>

                        {/* Client Profile Picture Section */}
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title d-flex align-items-center gap-2">
                              <FaUser /> Client Profile Picture
                            </h6>
                            <p className="text-muted small mb-3">
                              Upload a profile picture for the client
                            </p>
                            
                            <Row>
                              <Col md={6}>
                                <Form.Control
                                  type="file"
                                  accept="image/*"
                                  onChange={handleClientProfilePictureSelect}
                                  className="mb-3"
                                  style={{
                                    height: '58px',
                                    fontSize: '1rem',
                                    fontWeight: '400',
                                    lineHeight: '1.5',
                                    color: '#212529',
                                    backgroundColor: '#fff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
                                  }}
                                />
                                {selectedClientProfilePicture && (
                                  <div className="mb-3">
                                    <Badge bg="info" className="me-2">
                                      Profile picture selected: {selectedClientProfilePicture.name}
                                    </Badge>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={uploadClientProfilePicture}
                                      disabled={uploadingClientProfile}
                                    >
                                      {uploadingClientProfile ? (
                                        <>
                                          <Spinner animation="border" size="sm" className="me-2" />
                                          Uploading...
                                        </>
                                      ) : (
                                        <>
                                          <FaUpload /> Upload Profile Picture
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </Col>
                              <Col md={6}>
                                {/* Profile Picture Preview */}
                                {(selectedClientProfilePicture || eventData.clientProfilePicture) && (
                                  <div className="text-center">
                                    <img
                                      src={selectedClientProfilePicture ? 
                                        URL.createObjectURL(selectedClientProfilePicture) : 
                                        eventData.clientProfilePicture
                                      }
                                      alt="Client Profile"
                                      className="img-fluid rounded"
                                      style={{
                                        width: '150px',
                                        height: '150px',
                                        objectFit: 'cover',
                                        border: '3px solid #dee2e6'
                                      }}
                                    />
                                    <p className="text-muted small mt-2">
                                      {selectedClientProfilePicture ? 
                                        'Preview (not uploaded yet)' : 
                                        'Uploaded profile picture'
                                      }
                                    </p>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between mt-5 pt-4" style={{
                      borderTop: '1px solid rgba(0, 0, 0, 0.05)'
                    }}>
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate("/dashboard")}
                        className="d-flex align-items-center gap-2 fw-medium"
                        style={{
                          border: '2px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '12px 24px',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#cbd5e0';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                        }}
                      >
                        <FaArrowLeft /> Cancel
                      </Button>
                      <div className="d-flex gap-3">
                        <Button
                          variant="outline-primary"
                          onClick={() => setShowPreviewModal(true)}
                          className="d-flex align-items-center gap-2 fw-medium"
                          style={{
                            border: '2px solid #4facfe',
                            borderRadius: '12px',
                            padding: '12px 24px',
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 2px 8px rgba(79, 172, 254, 0.15)',
                            color: '#4facfe',
                            backgroundColor: 'white'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#4facfe';
                            e.target.style.color = 'white';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.color = '#4facfe';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(79, 172, 254, 0.15)';
                          }}
                        >
                          <FaEye /> Preview
                        </Button>
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading}
                          className="d-flex align-items-center gap-2 fw-medium"
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px 24px',
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            if (!loading) {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!loading) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                            }
                          }}
                        >
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" /> Creating...
                            </>
                          ) : (
                            <>
                              <FaSave /> Create Event
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Form>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </div>

      {/* Audio Recording Modal */}
      <Modal show={showAudioModal} onHide={() => setShowAudioModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record Audio Guide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <AudioRecorder
              onRecordingComplete={handleAudioRecording}
              audioTrackConstraints={{
                noiseSuppression: true,
                echoCancellation: true,
              }}
              downloadOnSavePress={false}
              downloadFileExtension="webm"
            />
            {eventData.audioGuide && (
              <div className="mt-4">
                <h6>Current Audio Guide:</h6>
                <ReactAudioPlayer
                  src={eventData.audioGuide}
                  controls
                  className="w-100"
                />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Event Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h4 className="mb-2">{eventData.title || "Event Title"}</h4>
              <p className="text-muted mb-4">{eventData.shortDescription || "Short description"}</p>
              
              <Row>
                <Col md={8}>
                  <h6 className="fw-bold">Description</h6>
                  <p className="text-muted">{eventData.description || "Full description will appear here"}</p>
                  
                  <h6 className="fw-bold mt-4">Location</h6>
                  <p className="text-muted mb-1">{eventData.location || "Location name"}</p>
                  <p className="text-muted mb-1">{eventData.address || "Address"}</p>
                  <p className="text-muted mb-1">{eventData.city}, {eventData.province} {eventData.postalCode}</p>
                  <p className="text-muted">{eventData.country}</p>
                </Col>
                <Col md={4}>
                  <div className="card bg-light border-0">
                    <div className="card-body">
                      <h6 className="fw-bold">Details</h6>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Duration:</span>
                        <span className="fw-bold">{eventData.duration} hours</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Difficulty:</span>
                        <span className="fw-bold">{eventData.difficulty}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Capacity:</span>
                        <span className="fw-bold">{eventData.capacity} people</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Age Range:</span>
                        <span className="fw-bold">{eventData.ageMin}-{eventData.ageMax}</span>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
              
              {/* Pricing Section */}
              {eventData.prices && eventData.prices.length > 0 && (
                <div className="mt-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div style={{
                      width: '32px',
                      height: '32px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FaTag style={{ color: 'white', fontSize: '14px' }} />
                    </div>
                    <h6 className="fw-bold mb-0">Pricing Options</h6>
                  </div>
                  <div className="row">
                    {eventData.prices.map((price, index) => (
                      <div key={price.id} className="col-md-6 mb-3">
                        <div className={`card border-0 ${price.isActive ? 
                          'shadow-sm' : 'bg-light bg-opacity-50'}`} 
                          style={{
                            background: price.isActive ? 
                              'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' : 
                              'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                            boxShadow: price.isActive ? 
                              '0 4px 16px rgba(0, 0, 0, 0.08)' : 
                              '0 2px 8px rgba(0, 0, 0, 0.04)',
                            borderRadius: '12px',
                            border: price.isActive ? 
                              '1px solid rgba(102, 126, 234, 0.1)' : 
                              '1px solid rgba(0, 0, 0, 0.05)',
                            transition: 'all 0.2s ease-in-out'
                          }}>
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <h6 className="card-title mb-0 fw-semibold" style={{
                                  color: price.isActive ? '#2d3748' : '#718096'
                                }}>
                                  {price.name}
                                </h6>
                                {!price.isActive && (
                                  <Badge style={{
                                    background: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)',
                                    border: 'none',
                                    fontSize: '0.7rem',
                                    padding: '4px 8px',
                                    borderRadius: '6px'
                                  }}>
                                    Inactive
                                  </Badge>
                                )}
                              </div>
                              <span className="fw-bold fs-5" style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text'
                              }}>
                                {price.currency || eventData.currency} {price.amount.toFixed(2)}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <div style={{
                                width: '6px',
                                height: '6px',
                                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                                borderRadius: '50%',
                                boxShadow: '0 0 6px rgba(72, 187, 120, 0.4)'
                              }}></div>
                              <small className="text-muted fw-medium">
                                Valid: {price.startDate instanceof Date ? 
                                  price.startDate.toLocaleDateString() : 
                                  new Date(price.startDate).toLocaleDateString()
                                } - {price.endDate instanceof Date ? 
                                  price.endDate.toLocaleDateString() : 
                                  new Date(price.endDate).toLocaleDateString()
                                }
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {eventData.included.length > 0 && (
                <div className="mt-4">
                  <h6 className="fw-bold">What's Included</h6>
                  <ul className="list-unstyled">
                    {eventData.included.map((item, index) => (
                      <li key={index} className="mb-1">
                        <FaCheck className="text-success me-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {eventData.tags.length > 0 && (
                <div className="mt-4">
                  <h6 className="fw-bold">Tags</h6>
                  <div>
                    {eventData.tags.map((tag, index) => (
                      <Badge key={index} bg="primary" className="me-1">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Client Information */}
              {(eventData.clientName || eventData.clientEmail || eventData.clientPhone || eventData.clientDescription) && (
                <div className="mt-4">
                  <h6 className="fw-bold">Client Information</h6>
                  <div className="card bg-light border-0">
                    <div className="card-body">
                      <Row>
                        <Col md={3}>
                          {eventData.clientProfilePicture && (
                            <div className="text-center mb-3">
                              <img
                                src={eventData.clientProfilePicture}
                                alt="Client Profile"
                                className="img-fluid rounded"
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'cover',
                                  border: '2px solid #dee2e6'
                                }}
                              />
                            </div>
                          )}
                        </Col>
                        <Col md={9}>
                          {eventData.clientName && (
                            <div className="mb-2">
                              <strong>Name:</strong> {eventData.clientName}
                            </div>
                          )}
                          {eventData.clientEmail && (
                            <div className="mb-2">
                              <strong>Email:</strong> {eventData.clientEmail}
                            </div>
                          )}
                          {eventData.clientPhone && (
                            <div className="mb-2">
                              <strong>Phone:</strong> {eventData.clientPhone}
                            </div>
                          )}
                          {eventData.clientDescription && (
                            <div className="mb-2">
                              <strong>Description:</strong>
                              <p className="text-muted mb-0 mt-1">{eventData.clientDescription}</p>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
      </Modal>
    </>
  );
} 